import { inject, Injectable } from '@angular/core';

import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { UserStateService } from 'src/app/auth/services/user-state.service';
import { AnalyticsService } from './analytics.service';
import { BigQueryLoggingService } from './big-query-logging.service';

export type CompanySelectedSource = 'tree' | 'search' | 'parent-company';

@Injectable({
  providedIn: 'root',
})
export class TrackerService {
  private readonly userStateService = inject(UserStateService);
  private readonly companyStateService = inject(CompanyStateService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly bigQueryLoggingService = inject(BigQueryLoggingService);

  private logBigQueryEvent(
    eventName: string,
    eventDetails?: unknown,
    eventValue?: unknown,
    username?: string | null,
    companyId?: string | null,
  ): void {
    const result = this.bigQueryLoggingService.logEvent(
      eventName,
      eventDetails,
      eventValue,
      username,
      companyId,
    );

    if (result) {
      void result.catch(() => {});
    }
  }

  // openIdTracker
  OPENID_EVENTS_TO_BQ = ['silent renew error'];

  openidEvent(openidEventType, profile?, eventProperties?) {
    if (openidEventType) {
      profile = profile || {};
      eventProperties = eventProperties || {};

      var userId = this.userStateService.getUsername();
      var email = this.userStateService.getUserEmail() || profile.email;

      Object.assign(eventProperties, {
        openidEventType: openidEventType,
        userId: userId,
        email: email,
        googleUserId: profile.sub,
        companyId: this.companyStateService.getSelectedCompanyId(),
      });

      this.analyticsService.track('OpenId Event', eventProperties);

      if (this.OPENID_EVENTS_TO_BQ.indexOf(openidEventType) !== -1) {
        this.logBigQueryEvent(
          'OpenId silent renew error',
          eventProperties.errorMessage,
          null,
          userId || email,
        );
      }
    }
  }

  // purchaseFlowTracker
  productAddedEvent(plan) {
    this.analyticsService.track('Product Added', {
      id: plan.productCode,
      name: plan.name,
      price: plan.isMonthly ? plan.monthly.billAmount : plan.yearly.billAmount,
      displaysCount: plan.displays,
      category: 'Plans',
      inApp: false,
    });
  }

  placeOrderClickedEvent(properties) {
    this.analyticsService.track('Place Order Clicked', properties);
  }

  orderPayNowClickedEvent(properties) {
    this.analyticsService.track('Order Pay Now Clicked', properties);
  }

  // batchOperationsTracker
  batchOperationEvent(eventName?, operation?, items?, extraProperties?) {
    if (eventName) {
      var properties = extraProperties || {};
      properties.operationName = operation.name;
      properties.itemsCount = items.length;
      properties.companyId = this.companyStateService.getSelectedCompanyId();

      this.analyticsService.track(eventName, properties);
    }
  }

  // companyTracker
  COMPANY_EVENTS_TO_BQ = ['Company Created'];

  companyEvent(eventName?, companyId?, companyName?, isUserCompany?) {
    if (eventName) {
      this.analyticsService.track(eventName, {
        companyId: companyId,
        companyName: companyName,
        isUserCompany: isUserCompany,
      });
      if (this.COMPANY_EVENTS_TO_BQ.indexOf(eventName) !== -1) {
        this.logBigQueryEvent(
          eventName,
          companyName,
          null,
          this.userStateService.getUsername(),
          companyId,
        );
      }
    }
  }

  /**
   * `companyId` is the company being switched to, not the currently selected one.
   * Not mirrored to BigQuery.
   */
  companySelectedEvent(
    companyId: string,
    companyName: string,
    isUserCompany: boolean,
    source: CompanySelectedSource,
  ): void {
    this.analyticsService.track('Company Selected', {
      companyId,
      companyName,
      isUserCompany,
      source,
    });
  }

  // userTracker
  USER_EVENTS_TO_BQ = [];

  userEvent(eventName?, userId?, isSelf?, extraProperties?) {
    if (eventName) {
      var properties = extraProperties || {};
      properties.userId = userId;
      properties.companyId = this.companyStateService.getSelectedCompanyId();
      properties.isSelf = isSelf;

      this.analyticsService.track(eventName, properties);
      if (this.USER_EVENTS_TO_BQ.indexOf(eventName) !== -1) {
        this.logBigQueryEvent(eventName);
      }
    }
  }

  // presentationTracker
  PRESENTATION_EVENTS_TO_BQ = [
    'Presentation Created',
    'New Presentation',
    'Template Copied',
    'HTML Template Copied',
    'AI Chatbot Session Started',
    'AI Chatbot Message Sent',
    'AI Chatbot Response Received',
    'AI Chatbot Error',
    'AI Chatbot Session Cleared',
    'Presentation Changes Requested',
    'Presentation Review Requested',
    'Presentation Published',
  ];

  presentationEvent(
    eventName?,
    presentationId?,
    presentationName?,
    extraProperties?,
  ) {
    if (eventName) {
      var properties = extraProperties || {};
      properties.presentationId = presentationId;
      properties.presentationName = presentationName;
      properties.companyId = this.companyStateService.getSelectedCompanyId();

      this.analyticsService.track(eventName, properties);
      if (this.PRESENTATION_EVENTS_TO_BQ.indexOf(eventName) !== -1) {
        this.logBigQueryEvent(eventName, presentationId);
      }
    }
  }

  // scheduleTracker
  SCHEDULE_EVENTS_TO_BQ = ['Schedule Created'];

  scheduleEvent(eventName?, scheduleId?, scheduleName?, extraProperties?) {
    if (eventName) {
      var properties = extraProperties || {};
      properties.scheduleId = scheduleId;
      properties.scheduleName = scheduleName;
      properties.companyId = this.companyStateService.getSelectedCompanyId();

      this.analyticsService.track(eventName, properties);

      if (this.SCHEDULE_EVENTS_TO_BQ.indexOf(eventName) !== -1) {
        this.logBigQueryEvent(eventName, scheduleId);
      }
    }
  }

  // displayTracker
  DISPLAY_EVENTS_TO_BQ = ['Display Created'];

  displayEvent(eventName?, displayId?, displayName?, eventProperties?) {
    if (eventName) {
      eventProperties = eventProperties || {};
      Object.assign(eventProperties, {
        displayId: displayId,
        displayName: displayName,
        userId: this.userStateService.getUsername(),
        email: this.userStateService.getUserEmail(),
        companyId: this.companyStateService.getSelectedCompanyId(),
        companyName: this.companyStateService.getSelectedCompanyName(),
      });

      this.analyticsService.track(eventName, eventProperties);

      if (this.DISPLAY_EVENTS_TO_BQ.indexOf(eventName) !== -1) {
        this.logBigQueryEvent(eventName, displayId);
      }
    }
  }
}
